// Vendor Program v3 UAT recon (F9) — HTTP transport for the staff-editable
// ADVISORY vendor qualification stages. The server always returns the full
// 5-stage catalog (persisted assessments merged over it); the PUT records
// one stage. Hooks in @/hooks/portfolioAdmin/useVendorQualifications wrap
// these with TanStack Query.

import { api } from "@/api/client";

// --- Vocabulary (mirrors VendorQualificationStageStatuses server-side) -------

export type VendorQualificationStageStatus =
  | "pending"
  | "in-progress"
  | "complete"
  | "waived"
  | "not-applicable";

export const VENDOR_QUALIFICATION_STAGE_STATUSES: ReadonlyArray<{
  value: VendorQualificationStageStatus;
  label: string;
}> = [
  { value: "pending", label: "Pending" },
  { value: "in-progress", label: "In progress" },
  { value: "complete", label: "Complete" },
  { value: "waived", label: "Waived" },
  { value: "not-applicable", label: "Not applicable" },
];

export function vendorQualificationStatusLabel(status: string): string {
  return (
    VENDOR_QUALIFICATION_STAGE_STATUSES.find((s) => s.value === status)?.label ??
    status
  );
}

// --- Shapes -------------------------------------------------------------------

export interface VendorQualificationStageDto {
  stageCode: string;
  status: VendorQualificationStageStatus;
  statusDate: string | null;
  note: string | null;
  evidenceUrl: string | null;
  modifiedAt: string | null;
  /** Who last recorded this stage. Every stage here is a staff member's
   *  claim — nothing but staff writes these rows — so the surfaces name
   *  them. Null on a stage nobody has recorded. */
  recordedByUserId: string | null;
  /** Display name for `recordedByUserId`; null when it cannot be resolved,
   *  in which case surfaces say "a staff member" rather than print an id. */
  recordedByName: string | null;
  /** Step 5 W5 — whether the CURRENT status came from a person or an event.
   *  Optional so existing fixtures stay valid; the server always sends it. */
  statusSource?: "staff" | "system";
  /** Which event, when `statusSource` is "system". */
  statusSourceEvent?: string | null;
}

export interface UpdateVendorQualificationStageRequest {
  status: VendorQualificationStageStatus;
  statusDate?: string | null;
  note?: string | null;
  evidenceUrl?: string | null;
}

// --- Transport ------------------------------------------------------------------

export const vendorQualificationsApi = {
  list: (vendorId: string) =>
    api.get<VendorQualificationStageDto[]>(
      `/portfolio-admin/vendors/${vendorId}/qualification-stages`,
    ),
  upsert: (
    vendorId: string,
    stageCode: string,
    body: UpdateVendorQualificationStageRequest,
  ) =>
    api.put<VendorQualificationStageDto>(
      `/portfolio-admin/vendors/${vendorId}/qualification-stages/${stageCode}`,
      body,
    ),
};
